import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, TemplateRef, inject, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { WorkOrder } from '../model/workorder';
import { WorkOrderService } from '../../services/workorder.service';
import { Observable, Subject, debounceTime, distinctUntilChanged, map, of, takeUntil } from 'rxjs';
import { Store } from '@ngxs/store';
import { DealerState } from '../../../../shared/state/DealerState';
import { Dealer } from '../../../../shared/dealer/models/dealer';
import { PaginatedWoResponse } from '../model/paginated-wo.model';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { Router } from '@angular/router';
import { UpdateIsRestoreStateAction, WorkOrderState } from '../../../../shared/state/WorkOrderState';
import { WorkOrderFiltersModel } from '../model/work-order-filters.model';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { BreadcrumbContextService } from '../../../../shared/services/breadcrumb-context.service';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { ROLES } from '../../../authentication/authRoles.constants';
import { RouteTrackerService } from '../../services/route-tracker.service';
import { CLOSED, enableWorkOrderDelete, ENTERED, RELEASED } from '../../shared/util/work-order-helper-util';
import { SharedDeleteModalComponent } from '../../../../shared/components/shared-delete-modal/shared-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ColumnPreferencesService } from '../../../../shared/services/column-preferences.service';
import { ColumnPreferencesHelperService } from '../../../../shared/services/column-preferences-helper.service';
import { PaginationStateService } from '../../../search/services/pagination-state.service';
import { UserState } from '../../../../shared/state/UserState';

@Component({
  selector: 'app-workorderslist',
  templateUrl: './workorderslist.component.html',
  styleUrls: ['./workorderslist.component.scss'],
  standalone: false
})
export class WorkorderslistComponent implements OnInit, AfterViewInit, OnDestroy {
  // Inject dependencies first before using them in property initializers
  public store = inject(Store);
  public router = inject(Router);
  public dialog = inject(MatDialog);
  public translationService = inject(TranslationService);
  public workOrderService = inject(WorkOrderService);
  public breadcrumbContextService = inject(BreadcrumbContextService);
  public fb = inject(FormBuilder);
  public alertService = inject(AlertService);
  public featureflagsService = inject(FeatureflagsService);
  public routeTracker = inject(RouteTrackerService);
  protected readonly environment = environment;
  public columnPrefService = inject(ColumnPreferencesService);
  public cdr = inject(ChangeDetectorRef);
  public columnPrefsHelper = inject(ColumnPreferencesHelperService);
  public paginationStateService = inject(PaginationStateService);

  private readonly paginationUserId = this.getPaginationUserId();
  dealer: Dealer;
  userLogin: string;
  plantId: string;
  workOrderFilterForm: FormGroup;
  workOrderStatusList: { id: string; displayName: string }[];
  plantList = this.getPlantList();
  siteList = this.getSiteList();
  isRestoreState: boolean;
  currentPage = 1;
  pageSizeOptions = [10, 20, 50, 100];
  pageSize = this.columnPrefService.getPageSize('workorders-list') ?? 10;
  paginationStoragePrefix = `workorders-list:${this.paginationUserId}`;
  paginationStateStorageKey = `workorders-list-pagination-state:${this.paginationUserId}`;
  restoredPageFromState: number | null = null;
  pendingPaginatorSync = false;
  private readonly destroy$ = new Subject<void>();
  compareById = (a, b) => a && b && a.id === b.id;
  isCreateWorkOrderEnabled = false;
  isDeleteWorkOrderEnabled = false;
  protected readonly ROLES = ROLES;
  isWorkOrderRoute: boolean = true;

  // column preferences/state for selector
  private canonicalColumns: Record<string, any> = {};
  columnKeys: string[] = [];
  columnOrder: string[] = [];
  columnVisibility: Record<string, boolean> = {};
  columnNames: Record<string, string> = {};

  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Element>;
  @ViewChild('customerModal') customerModal: ModalComponent;
  @ViewChild('customerModalContent') customerModalContent: TemplateRef<any>;

  private getPaginationUserId(): string {
    try {
      return this.store.selectSnapshot(UserState.getUserName) || 'anon';
    } catch {
      return 'anon';
    }
  }

  tableConfig: TableConfig<WorkOrder> = {
    sort: {
      default: 'receivedDate:desc',
      resetPaginationOnSortChange: true
    },
    columns: {
      workOrderNumber: {
        cell: (item: WorkOrder) => item.workOrderNumber,
        header: this.translationService.translate('workOrderNumberText'),
        sortable: true
      },
      workOrderStatus: {
        cell: 'workOrderStatusCell',
        header: this.translationService.translate('statusColumnText'),
        sortable: true
      },
      numberOfLines: {
        cell: 'numberOfLinesCell',
        header: this.translationService.translate('numberOfLinesText')
      },
      customerCode: {
        cell: (item: WorkOrder) => item.customerCode,
        header: this.translationService.translate('customerCodeText')
      },
      customerName: {
        cell: (item: WorkOrder) => item.customerName,
        header: this.translationService.translate('customerNameText')
      },
      crn: {
        cell: (item: WorkOrder) => item.crn,
        header: this.translationService.translate('CRNText')
      },
      plant: {
        cell: (item: WorkOrder) => item.plant,
        header: this.translationService.translate('plantText')
      },
      site: {
        cell: (item: WorkOrder) => item.site,
        header: this.translationService.translate('siteText')
      },
      receivedDate: {
        cell: (item: WorkOrder) => item.receivedDate || '',
        header: this.translationService.translate('receivedText'),
        sortable: true
      },
      dueDate: {
        cell: (item: WorkOrder) => item.dueDate || '',
        header: this.translationService.translate('dueText'),
        sortable: true
      }
    },
    paginator: {
      style: 'pageNumber',
      options: {
        nbPagesToShow: 5
      },
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    storage: {
      type: localStorage,
      options: {
        prefix: this.paginationStoragePrefix,
        paginator: true,
        columns: false,
        tags: false
      }
    },
    onRowClick: (workOrder: WorkOrder) => {
      this.router.navigate(['/home/work-orders/work-orders-details', workOrder.id]);
    }
  };

  constructor() {
    this.dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.isDeleteWorkOrderEnabled = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.DELETE_WORK_ORDER_ENABLED);
    this.workOrderStatusList = this.getWorkOrderStatus();
    this.workOrderFilterForm = this.fb.group({
      workOrderNumber: [''],
      workOrderStatus: [this.workOrderStatusList],
      workOrderPlant: [this.plantList],
      workOrderSite: [this.siteList],
      customerCode: [''],
      customerName: [''],
      crn: [''],
      workOrderReceivedDateStart: [''],
      workOrderReceivedDateEnd: [''],
      workOrderDueDateStart: [''],
      workOrderDueDateEnd: ['']
    });
    this.isRestoreState = this.store.selectSnapshot(WorkOrderState.isRestoreState) || false;
    this.isWorkOrderRoute = this.routeTracker.getPreviousUrl()?.includes('/home/work-orders') || false;
    if (this.isRestoreState && this.isWorkOrderRoute) {
      this.pendingPaginatorSync = true;
      const savedPaginationState = this.paginationStateService.getPaginationState(this.paginationStateStorageKey);
      if (savedPaginationState) {
        this.currentPage = savedPaginationState.currentPage || this.currentPage;
        this.pageSize = savedPaginationState.pageSize || this.pageSize;
        this.restoredPageFromState = this.currentPage;
      }
    }
    if (!(this.isRestoreState && this.isWorkOrderRoute)) {
      this.clearPaginationStorage();
      this.paginationStateService.clearPaginationState(this.paginationStateStorageKey);
    }
  }

  ngOnInit(): void {
    // Initialize feature flag
    this.isCreateWorkOrderEnabled = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.CREATE_WORK_ORDER_ENABLED);

    // Clear any existing casing search context when navigating to work orders from menu
    this.breadcrumbContextService.clearNavigationContext();

    // Initialize tableConfig with translated headers
    this.tableConfig = {
      sort: {
        default: 'receivedDate:desc',
        resetPaginationOnSortChange: true
      },
      columns: {
        workOrderNumber: {
          cell: (item: WorkOrder) => item.workOrderNumber,
          header: this.translationService.translate('workOrderNumberText'),
          sortable: true
        },
        workOrderStatus: {
          cell: 'workOrderStatusCell',
          header: this.translationService.translate('statusColumnText'),
          sortable: true
        },
        numberOfLines: {
          cell: 'numberOfLinesCell',
          header: this.translationService.translate('numberOfLinesText')
        },
        customerCode: {
          cell: (item: WorkOrder) => item.customerCode,
          header: this.translationService.translate('customerCodeText')
        },
        customerName: {
          cell: (item: WorkOrder) => item.customerName,
          header: this.translationService.translate('customerNameText')
        },
        crn: {
          cell: (item: WorkOrder) => item.crn,
          header: this.translationService.translate('CRNText')
        },
        plant: {
          cell: (item: WorkOrder) => item.plant,
          header: this.translationService.translate('plantText')
        },
        site: {
          cell: (item: WorkOrder) => item.site,
          header: this.translationService.translate('siteText')
        },
        receivedDate: {
          cell: (item: WorkOrder) => item.receivedDate || '',
          header: this.translationService.translate('receivedText'),
          sortable: true
        },
        dueDate: {
          cell: (item: WorkOrder) => item.dueDate || '',
          header: this.translationService.translate('dueText'),
          sortable: true
        }
      },
      paginator: {
        style: 'pageNumber',
        options: {
          nbPagesToShow: 5
        },
        pageSize: this.pageSize,
        pageSizeOptions: this.pageSizeOptions,
        forceBottomAction: true,
        position: 'bottom'
      },
      storage: {
        type: localStorage,
        options: {
          prefix: this.paginationStoragePrefix,
          paginator: true,
          columns: false,
          tags: false
        }
      },
      onRowClick: (workOrder: WorkOrder) => {
        this.router.navigate(['/home/work-orders/work-orders-details', workOrder.id]);
      }
    };

    this.tableConfig = {
      sort: {
        default: 'receivedDate:desc',
        resetPaginationOnSortChange: true
      },
      columns: {
        workOrderNumber: {
          cell: (item: WorkOrder) => item.workOrderNumber,
          header: this.translationService.translate('workOrderNumberText'),
          sortable: true
        },
        workOrderStatus: {
          cell: 'workOrderStatusCell',
          header: this.translationService.translate('statusColumnText'),
          sortable: true
        },
        numberOfLines: {
          cell: 'numberOfLinesCell',
          header: this.translationService.translate('numberOfLinesText')
        },
        customerCode: {
          cell: (item: WorkOrder) => item.customerCode,
          header: this.translationService.translate('customerCodeText')
        },
        customerName: {
          cell: (item: WorkOrder) => item.customerName,
          header: this.translationService.translate('customerNameText')
        },
        crn: {
          cell: (item: WorkOrder) => item.crn,
          header: this.translationService.translate('CRNText')
        },
        plant: {
          cell: (item: WorkOrder) => item.plant,
          header: this.translationService.translate('plantText')
        },
        site: {
          cell: (item: WorkOrder) => item.site,
          header: this.translationService.translate('siteText')
        },
        receivedDate: {
          cell: (item: WorkOrder) => item.receivedDate || '',
          header: this.translationService.translate('receivedText'),
          sortable: true
        },
        dueDate: {
          cell: (item: WorkOrder) => item.dueDate || '',
          header: this.translationService.translate('dueText'),
          sortable: true
        }
      },
      paginator: {
        style: 'pageNumber',
        options: {
          nbPagesToShow: 5
        },
        pageSize: this.pageSize,
        pageSizeOptions: this.pageSizeOptions,
        forceBottomAction: true,
        position: 'bottom'
      },
      storage: {
        type: localStorage,
        options: {
          prefix: this.paginationStoragePrefix,
          paginator: true,
          columns: false,
          tags: false
        }
      },
      onRowClick: (workOrder: WorkOrder) => {
        this.router.navigate(['/home/work-orders/work-orders-details', workOrder.id]);
      }
    };

    this.dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.userLogin = this.store.selectSnapshot(DealerState.currentUser);
    this.workOrderFilterForm.valueChanges.pipe(debounceTime(700), distinctUntilChanged()).subscribe(() => {
      // Use getWorkOrderFilters() to produce the normalized filter model (includes searchByUser)
      this.tableRef.patchExtraFilters(this.getWorkOrderFilters(), true);
    });
    this.tableConfig.onColumnsChange = (columns: string[]) => {
      // Set display property for all columns based on columns array
      Object.keys(this.tableConfig.columns).forEach((key) => {
        this.tableConfig.columns[key].display = columns.includes(key);
      });
    };

    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      // build canonical columns with translated headers
      this.canonicalColumns = {
        workOrderNumber: {
          cell: (item: WorkOrder) => item.workOrderNumber,
          header: this.translationService.translate('workOrderNumberText'),
          sortable: true
        },
        workOrderStatus: {
          cell: 'workOrderStatusCell',
          header: this.translationService.translate('statusColumnText'),
          sortable: true
        },
        numberOfLines: {
          cell: 'numberOfLinesCell',
          header: this.translationService.translate('numberOfLinesText')
        },
        customerCode: {
          cell: (item: WorkOrder) => item.customerCode,
          header: this.translationService.translate('customerCodeText')
        },
        customerName: {
          cell: (item: WorkOrder) => item.customerName,
          header: this.translationService.translate('customerNameText')
        },
        crn: {
          cell: (item: WorkOrder) => item.crn,
          header: this.translationService.translate('CRNText')
        },
        plant: {
          cell: (item: WorkOrder) => item.plant,
          header: this.translationService.translate('plantText')
        },
        site: {
          cell: (item: WorkOrder) => item.site,
          header: this.translationService.translate('siteText')
        },
        receivedDate: {
          cell: (item: WorkOrder) => item.receivedDate || '',
          header: this.translationService.translate('receivedText'),
          sortable: true
        },
        dueDate: {
          cell: (item: WorkOrder) => item.dueDate || '',
          header: this.translationService.translate('dueText'),
          sortable: true
        }
      };

      // map column keys to labels for selector
      this.columnKeys = Object.keys(this.canonicalColumns);
      this.columnNames = this.columnKeys.reduce((acc, k) => {
        acc[k] = (this.canonicalColumns[k] && this.canonicalColumns[k].header) || k;
        return acc;
      }, {} as Record<string, string>);

      // initialize columns with user preferences
      const init = this.columnPrefsHelper.initialize('workorders-list', this.canonicalColumns);
      this.tableConfig.columns = init.tableColumns;
      this.columnNames = init.columnNames;
      this.columnKeys = init.columnKeys;
      this.columnOrder = init.columnOrder;
      this.columnVisibility = init.columnVisibility;

      this.workOrderStatusList = this.getWorkOrderStatus();
      const current = this.workOrderFilterForm?.get('workOrderStatus')?.value;
      const selectedIds = current.map((s: any) => (s && s.id ? s.id : s));
      const newSelection = this.workOrderStatusList.filter((s) => selectedIds.includes(s.id));
      // Patch form control without emitting valueChanges to avoid triggering filters immediately
      this.workOrderFilterForm.patchValue({ workOrderStatus: newSelection }, { emitEvent: false });
      this.cdr.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    // Restored data can be emitted synchronously before this hook runs, so re-apply the
    // persisted page immediately and again once the first data batch lands (total-count
    // updates reset the page-number indicator back to page 1).
    setTimeout(() => this.syncPaginatorPageIndicator());

    this.tableRef?.paginator?.dataChanged.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.pendingPaginatorSync) {
        return;
      }
      setTimeout(() => {
        this.syncPaginatorPageIndicator();
        this.pendingPaginatorSync = false;
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private syncPaginatorPageIndicator(targetPage?: number): void {
    const paginator = this.tableRef?.paginator;
    const targetIndex = targetPage != null ? Math.max(targetPage - 1, 0) : paginator?.pageIndex ?? 0;
    if (!paginator || !targetIndex) {
      return;
    }

    const paginatorRefs = [paginator.paginatorRef1, paginator.paginatorRef2].filter(Boolean);
    paginatorRefs.forEach((ref) => {
      ref.pageIndex = 0;
    });

    setTimeout(() => {
      paginatorRefs.forEach((ref) => {
        ref.pageIndex = targetIndex;
      });
    });
  }

  clearSelections(): void {
    // Reset all form controls to initial state
    this.workOrderFilterForm.patchValue({
      workOrderNumber: '',
      workOrderStatus: this.workOrderStatusList,
      workOrderPlant: this.plantList,
      workOrderSite: this.siteList,
      customerCode: '',
      customerName: '',
      crn: '',
      workOrderReceivedDateStart: '',
      workOrderReceivedDateEnd: '',
      workOrderDueDateStart: '',
      workOrderDueDateEnd: ''
    });
    // Refresh the table by updating filters
    this.tableRef.patchExtraFilters(this.workOrderFilterForm.value, true);
  }

  canCreateWorkOrders(): boolean {
    const roles: string = localStorage.getItem('roles') || '';
    return roles.includes(ROLES.create_work_orders);
  }

  navigateToCreateWorkOrder(): void {
    this.router.navigate(['/home/work-orders/create']);
  }

  deleteWorkOrder(workOrder: WorkOrder): void {
    console.log('Deleting work order:', workOrder);
  }

  getWorkOrderFilters(): WorkOrderFiltersModel {
    const formValues = this.workOrderFilterForm.value;
    return {
      workOrderNumber: formValues.workOrderNumber || '',
      workOrderStatus: Array.isArray(formValues.workOrderStatus)
        ? formValues.workOrderStatus.length > 4
          ? []
          : formValues.workOrderStatus.map((status: any) => (status && status.id ? status.id : status))
        : formValues.workOrderStatus || [],
      workOrderPlant: Array.isArray(formValues.workOrderPlant)
        ? formValues.workOrderPlant.length > this.plantList.length - 1
          ? []
          : formValues.workOrderPlant.map((plant: any) => plant.id)
        : formValues.workOrderPlant || [],
      workOrderSite: Array.isArray(formValues.workOrderSite)
        ? formValues.workOrderSite.length > this.siteList.length - 1
          ? []
          : formValues.workOrderSite.map((site: any) => site.id)
        : formValues.workOrderSite || [],
      customerCode: formValues.customerCode || '',
      customerName: formValues.customerName || '',
      crn: (formValues.crn || '').trim(),
      workOrderReceivedDateStart: formValues.workOrderReceivedDateStart || '',
      workOrderReceivedDateEnd: formValues.workOrderReceivedDateEnd || '',
      workOrderDueDateStart: formValues.workOrderDueDateStart || '',
      workOrderDueDateEnd: formValues.workOrderDueDateEnd || '',
      searchByUser: this.userLogin || ''
    };
  }

  /**
   * Maps stored filters (which contain IDs only) back to form format (with full objects)
   * This is needed when restoring filters from the store after navigation
   */
  private mapStoredFiltersToFormFormat(storedFilters: WorkOrderFiltersModel): any {
    if (!storedFilters) {
      return {
        workOrderNumber: '',
        workOrderStatus: this.workOrderStatusList,
        workOrderPlant: this.plantList,
        workOrderSite: this.siteList,
        customerCode: '',
        customerName: '',
        crn: '',
        workOrderReceivedDateStart: '',
        workOrderReceivedDateEnd: '',
        workOrderDueDateStart: '',
        workOrderDueDateEnd: ''
      };
    }

    // Map workOrderStatus IDs back to full objects
    const workOrderStatusObjects = (storedFilters.workOrderStatus || [])
      .map((statusId: string) => this.workOrderStatusList.find((status) => status.id === statusId))
      .filter((status): status is { id: string; displayName: string } => !!status);

    // Map workOrderPlant IDs back to full objects
    const workOrderPlantObjects = (storedFilters.workOrderPlant || [])
      .map((plantId: string) => this.plantList.find((plant) => plant.id === plantId))
      .filter((plant): plant is { id: string; displayName: string } => !!plant);

    // Map workOrderSite IDs back to full objects
    const workOrderSiteObjects = (storedFilters.workOrderSite || [])
      .map((siteId: string) => this.siteList.find((site) => site.id === siteId))
      .filter((site): site is { id: string; displayName: string } => !!site);

    return {
      workOrderNumber: storedFilters.workOrderNumber || '',
      workOrderStatus: workOrderStatusObjects.length > 0 ? workOrderStatusObjects : this.workOrderStatusList,
      workOrderPlant: workOrderPlantObjects.length > 0 ? workOrderPlantObjects : this.plantList,
      workOrderSite: workOrderSiteObjects.length > 0 ? workOrderSiteObjects : this.siteList,
      customerCode: storedFilters.customerCode || '',
      customerName: storedFilters.customerName || '',
      crn: storedFilters.crn || '',
      workOrderReceivedDateStart: storedFilters.workOrderReceivedDateStart || '',
      workOrderReceivedDateEnd: storedFilters.workOrderReceivedDateEnd || '',
      workOrderDueDateStart: storedFilters.workOrderDueDateStart || '',
      workOrderDueDateEnd: storedFilters.workOrderDueDateEnd || ''
    };
  }

  fetcher: PaginatorFetcher<WorkOrder> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.columnPrefService.savePageSize('workorders-list', filters.limit);
    this.savePaginationState(filters.sort);

    if (this.isRestoreState && this.isWorkOrderRoute) {
      const items: WorkOrder[] = this.store.selectSnapshot(WorkOrderState.currentPaginatedWorkOrders) || [];
      const count: number = this.store.selectSnapshot(WorkOrderState.totalRecords) || 0;
      const storedFilters = this.store.selectSnapshot(WorkOrderState.workOrderFilters);

      // Map the stored filters back to the proper format with full objects
      const restoredFilters = this.mapStoredFiltersToFormFormat(storedFilters);
      this.workOrderFilterForm.patchValue(restoredFilters, { emitEvent: false });

      this.isRestoreState = false;
      this.store.dispatch(new UpdateIsRestoreStateAction(this.isRestoreState));
      this.savePaginationState(filters.sort, count);
      return of({ items, count });
    } else {
      const workOrderFilters = this.getWorkOrderFilters();
      return this.workOrderService.searchWorkOrders(this.dealer.id, workOrderFilters, filters.page, filters.limit, filters.sort).pipe(
        map((response: PaginatedWoResponse) => {
          if (response && response.error && response.error.length > 0) {
            this.alertService.showAlert({
              type: AlertType.strong,
              content: this.translationService.translate('tryAgainText')
            } as Alert);
            return;
          } else {
            const items: WorkOrder[] = response.data.workorders || [];
            const count = response.data.pagination.totalRecords || 0;
            this.savePaginationState(filters.sort, count);
            // State is now managed by the service, no need to dispatch here
            return { items, count };
          }
        })
      );
    }
  };

  private clearPaginationStorage(): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.paginationStoragePrefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }

  private savePaginationState(sort: string | null = null, totalRecords?: number): void {
    const [sortField, sortDirection] = sort ? sort.split(':') : ['receivedDate', 'desc'];
    this.paginationStateService.savePaginationState(
      {
        currentPage: this.currentPage,
        pageSize: this.pageSize,
        sortField: sortField || 'receivedDate',
        sortDirection: (sortDirection || 'desc').toUpperCase(),
        totalRecords: totalRecords ?? (this.store.selectSnapshot(WorkOrderState.totalRecords) || 0)
      },
      this.paginationStateStorageKey
    );
  }

  openCustomerModal(): void {
    this.customerModal.open({
      id: 'customer-select',
      width: 800,
      title: 'Select Customer',
      content: this.customerModalContent,
      confirm: 'Close'
    });
  }

  onCustomerSelected(customer: any): void {
    if (customer) {
      this.workOrderFilterForm.patchValue({
        customerCode: customer.code,
        customerName: customer.customerName
      });
      if (this.customerModal && this.customerModal.dialog) {
        this.customerModal.dialog.closeAll();
      }
    }
  }

  isWorkOrderDeletable(workOrder: WorkOrder): boolean {
    if (workOrder.workOrderStatus == RELEASED || workOrder.workOrderStatus == ENTERED) {
      return workOrder.numberOfLines === 0;
    }
    if (workOrder.workOrderStatus == CLOSED) {
      return workOrder.isAllCasingDeleted || workOrder.numberOfLines === 0;
    }
    return false;
  }

  private getWorkOrderStatus(): { id: string; displayName: string }[] {
    // Return a list of objects where `id` is the original status code (used for filtering)
    // and `displayName` is the translated label shown to the user.
    const baseStatuses = [
      { id: 'ENTERED', displayName: this.translationService.translate('enteredText') },
      { id: 'RELEASED', displayName: this.translationService.translate('releasedText') },
      { id: 'IN_PROGRESS', displayName: this.translationService.translate('inProgressText') },
      { id: 'COMPLETED', displayName: this.translationService.translate('completedText') },
      { id: 'CLOSED', displayName: this.translationService.translate('closedText') }
    ];

    if (this.dealer.displayDeleteCasing === 'Y' && this.isDeleteWorkOrderEnabled) {
      return [...baseStatuses, { id: 'DELETED', displayName: this.translationService.translate('deletedText') }];
    }
    return baseStatuses;
  }

  // Return a translated label for the status or raw status if no translation exists
  getStatusLabel(status: string): string {
    if (!status) return '-';
    const statusLabels: { [key: string]: string } = {
      ENTERED: this.translationService.translate('enteredText'),
      RELEASED: this.translationService.translate('releasedText'),
      IN_PROGRESS: this.translationService.translate('inProgressText'),
      COMPLETED: this.translationService.translate('completedText'),
      CLOSED: this.translationService.translate('closedText')
    };
    const key = (status || '').trim().toUpperCase().replace(/\s+/g, '_');
    return statusLabels[key] || status;
  }

  // Map status text to mic-status CSS classes (workorders-specific mapping)
  getStatusClass(status: string): string {
    if (!status) return '';
    const mapping: { [key: string]: string } = {
      ENTERED: '', // white / no class
      RELEASED: 'primary',
      IN_PROGRESS: 'yellow',
      COMPLETED: 'valid',
      CLOSED: 'valid'
    };
    const key = (status || '').trim().toUpperCase().replace(/\s+/g, '_');
    return mapping[key] ?? '';
  }

  getStatusTooltip(status: string): string {
    if (!status) return '';
    const tooltipMapping: { [key: string]: string } = {
      ENTERED: this.translationService.translate('workOrderStatusEnteredTooltip'),
      RELEASED: this.translationService.translate('workOrderStatusReleasedTooltip'),
      IN_PROGRESS: this.translationService.translate('workOrderStatusInProgressTooltip'),
      COMPLETED: this.translationService.translate('workOrderStatusCompletedTooltip'),
      CLOSED: this.translationService.translate('workOrderStatusClosedTooltip')
    };
    const key = (status || '').trim().toUpperCase().replace(/\s+/g, '_');
    return tooltipMapping[key] ?? '';
  }

  private getPlantList(): { id: string; displayName: string }[] {
    const plants = this.store.selectSnapshot(DealerState.plants);
    return plants?.length > 0
      ? plants.map((plant) => ({
          id: plant.id,
          displayName: plant.code + '-' + plant.name
        }))
      : [];
  }

  private getSiteList(): { id: string; displayName: string }[] {
    const sites = this.store.selectSnapshot(DealerState.sites);
    return sites?.length > 0
      ? sites.map((site) => ({
          id: site.id,
          displayName: site.code + '-' + site.name
        }))
      : [];
  }

  onDeleteClick(workOrder: WorkOrder): void {
    this.dialog
      .open(SharedDeleteModalComponent, {
        data: {
          title: this.translationService.translate('deleteWorkOrderText'),
          messageTitle: this.translationService.translate('warningText'),
          message: this.translationService.translate('deleteWorkOrderDialogText'),
          buttonText: this.translationService.translate('yesDeleteText')
        }
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === true) {
          this.workOrderService.markDeleteAndSaveWorkOrder(this.dealer.id, workOrder.id, this.userLogin).subscribe({
            next: () => {
              this.alertService.showAlert({
                type: AlertType.success,
                content: this.translationService.translate('workOrderDeleteSuccessfulText')
              } as Alert);
              this.tableRef?.resetFilters();
            },
            error: () =>
              this.alertService.showAlert({
                type: AlertType.strong,
                content: this.translationService.translate('workOrderDeleteErrorText')
              } as Alert)
          });
        }
      });
  }

  openExportModal(): void {
    const columns: ExportColumnDef[] = [
      { key: 'workOrderNumber', label: this.translationService.translate('workOrderNumberText') },
      { key: 'workOrderStatus', label: this.translationService.translate('workOrderStatusText') },
      { key: 'customerCode', label: this.translationService.translate('customerCodeText') },
      { key: 'customerName', label: this.translationService.translate('customerNameText') },
      { key: 'crn', label: this.translationService.translate('CRNText') },
      { key: 'plant', label: this.translationService.translate('plantText') },
      { key: 'site', label: this.translationService.translate('siteText') },
      { key: 'receivedDate', label: this.translationService.translate('receivedText') },
      { key: 'dueDate', label: this.translationService.translate('dueText') },
      { key: 'numberOfLines', label: this.translationService.translate('numberOfLinesText') }
    ];
    const dataFetcher = (): Observable<ExportResponse> => {
      const filters = this.store.selectSnapshot(WorkOrderState.workOrderFilters);
      const dealer = this.store.selectSnapshot(DealerState.currentDealer);
      if (!filters || !dealer) {
        return of({ data: [] });
      }
      return this.workOrderService.exportWorkOrders(dealer.id, filters, 1, 10000, 'receivedDate:desc').pipe(
        map((response: PaginatedWoResponse) => ({
          data: (response?.data?.workorders || []) as Record<string, any>[]
        })),
        catchError(() => of({ data: [] }))
      );
    };
    const data: ExportModalData = {
      columns,
      dataFetcher,
      fileNamePrefix: 'workorder-export',
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '600px' });
  }

  protected readonly enableWorkOrderDelete = enableWorkOrderDelete;

  // Toggle visibility for a column and persist preferences (delegates to helper)
  toggleColumn(key: string): void {
    const result = this.columnPrefsHelper.toggleAndSave(
      'workorders-list',
      key,
      this.canonicalColumns,
      this.columnOrder,
      this.columnVisibility
    );
    this.tableConfig.columns = result.tableColumns;
    this.columnVisibility = result.columnVisibility;
    this.cdr.markForCheck();
  }
}
